import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {getTeammates} from '../../../../store/data/teammates/teammatesSelectors';
import {
  setIsConversationDetailsSidePanelOpenAction,
  setIsSidePanelOpenAction,
} from '../../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {getSelectedConversation} from '../../../../store/panels/analytics/details/analyticsPanelDetailsSelectors';
import {popoutConversationViewerAction} from '../../../../store/panels/viewer/viewerPanelActions';
import {renderReportSidePanelCloseButton} from '../reportSidePanelHelpers';
import {ConversationDetailsHeaderTags} from './conversationDetailsHeaderTags';
import {ConversationDetailsHeaderTeammates} from './conversationDetailsHeaderTeammates';

/*
 * Styles.
 */

const StyledHeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  padding: 20px 16px;
`;

const StyledHeaderNavigation = styled.div`
  ${flexCenter}
  margin-bottom: 8px;
`;

const StyledHeaderTitle = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
  margin-left: 6px;
  flex: 1;
`;

const StyledConversationSubject = styled.div`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.veryLarge};
  color: ${(p) => p.theme.greys.shade90};
  margin-bottom: 8px;
  line-height: 28px;
`;

const StyledPopoutButton = styled(IconButton)`
  margin-right: 8px;
`;

/*
 * Component.
 */

export const ConversationDetailsHeader: FC = () => {
  const dispatch = useAppDispatch();

  const selectedConversation = useAppSelector(getSelectedConversation);
  const teammatesResourceMap = useAppSelector(getTeammates);

  if (!selectedConversation) {
    return null;
  }

  const onRequestBack = () => {
    dispatch(setIsConversationDetailsSidePanelOpenAction(false));
  };

  const onRequestPopout = () => {
    dispatch(popoutConversationViewerAction(selectedConversation.id));
  };

  const onRequestClose = () => {
    dispatch(setIsSidePanelOpenAction(false));
  };

  return (
    <StyledHeaderWrapper>
      <StyledHeaderNavigation>
        {renderBackButton(onRequestBack)}
        {renderTitle()}
        {renderPopoutButton(onRequestPopout)}
        {renderReportSidePanelCloseButton(onRequestClose)}
      </StyledHeaderNavigation>
      <ConversationDetailsHeaderTeammates
        selectedConversation={selectedConversation}
        teammatesResourceMap={teammatesResourceMap}
      />
      <StyledConversationSubject>{selectedConversation.subject}</StyledConversationSubject>
      <ConversationDetailsHeaderTags tags={selectedConversation.tags} />
    </StyledHeaderWrapper>
  );
};

/*
 * Helpers.
 */

function renderTitle() {
  return (
    <StyledHeaderTitle>
      <Trans id="analytics-conversation-details-panel-title">Conversation details</Trans>
    </StyledHeaderTitle>
  );
}

function renderBackButton(onRequestBack: Handler) {
  return (
    <IconButton
      iconName="chevronLeftSmall"
      onClick={onRequestBack}
      label={<Trans id="analytics-conversation-details-panel-back">Back</Trans>}
    />
  );
}

function renderPopoutButton(onRequestPopout: Handler) {
  return (
    <StyledPopoutButton
      iconName="popout"
      onClick={onRequestPopout}
      label={<Trans id="analytics-conversation-details-panel-popout">View in external window</Trans>}
    />
  );
}
